import { Match, Show, Switch } from "solid-js";
import { SwipeButton } from "~/widgets/swipe_button/swipe_button";
import { ConfirmationDialog } from "~/components/brand_l2/confirmation_dialog";
import { toIndianNumber, toRupees } from "~/utils/number";
import { coinIcon } from "~/assets/assets";
import { useModal } from "~/components/modal";
import { PhosphorIcon } from "~/widgets/icons";
import { HubbleEvent } from "~/data/events";
import { useDenominationGridContext } from "../context/denomination_grid_context";
import { DottedLoader } from "~/widgets/loader";
import { isMobile } from "~/utils/platform";
import { useAnalyticsService } from "~/context/analytics_service_context";
import { clientIdConfig } from "~/data/partner_config";

export function ProceedButton() {
  const gridState = useDenominationGridContext();
  const { enoughCoinsAvailable, externalProps, internalState } = gridState;
  const { updateModalContentGetter, setIsModalOn, setOuterFrameClass } =
    useModal()!;

  const analyticsService = useAnalyticsService();

  const {
    isCoinsOnly,
    brand,
    isRewardTypePostPaid,
    offerConstruct,
    isDarkTheme,
    initiateOrder,
    ctaCopy,
    getClientId,
  } = externalProps;

  const {
    selectedAmount,
    ctaState,
    dryRunErrorMessage: errorMessage,
    dueAmount,
    rewardDiscountAmount,
  } = internalState;

  // not showing swipe button for visit health rewards client id,
  // becuase it is opened in mobile view on desktop, and swipe button does not work on desktop
  if (
    isCoinsOnly &&
    getClientId() !== clientIdConfig.visitHealthRewardsClientId
  ) {
    return <CoinsOnlyPaymentCta />;
  }

  return <GetThisCardButton onClick={onClickGetThisCard} />;

  function CoinsOnlyPaymentCta() {
    return (
      <>
        <div class="w-full md:hidden">
          <Switch>
            <Match when={isPaymentCtaEnabled()}>
              <SwipeButton
                isLoading={() => {
                  return ctaState() === "LOADING";
                }}
                isEnabled={isPaymentCtaEnabled}
                onSwipe={onClickGetThisCard}
                loaderColor={isDarkTheme ? "#000" : "#fff"}
                isDarkTheme={isDarkTheme}
              />
            </Match>
            <Match when={true}>
              <GetThisCardButton onClick={() => {}} />
            </Match>
          </Switch>
        </div>
        <div class="hidden w-full md:flex">
          <GetThisCardButton onClick={onClickGetThisCard} />
        </div>
      </>
    );
  }

  function GetThisCardButton(props: { onClick: () => void }) {
    return (
      <Switch>
        <Match when={ctaState() == "LOADING"}>
          <div class="button-filled inline-flex h-12 w-full cursor-pointer items-center justify-center gap-2.5 self-stretch rounded-3xl bg-neutral-800 px-4 py-2.5 opacity-40">
            <div class="text-mediumBold text-baseTertiaryLight">
              <DottedLoader color="#fff" />
            </div>
          </div>
        </Match>
        <Match when={true}>
          {
            <button
              class={`button-filled inline-flex h-12 w-full  items-center justify-between gap-2.5 self-stretch rounded-3xl bg-basePrimaryDark px-6 py-2.5 ${isPaymentCtaEnabled() ? "cursor-pointer" : "cursor-not-allowed"}`}
              classList={{
                "opacity-40 dark:bg-basePrimaryDark  dark:opacity-100":
                  !isPaymentCtaEnabled(),
              }}
              onClick={() => {
                if (!isPaymentCtaEnabled()) {
                  return;
                }
                if (ctaState() == "DEFAULT") {
                  props.onClick();
                }
              }}
            >
              <CtaContent />
            </button>
          }
        </Match>
      </Switch>
    );
  }

  function CtaContent() {
    if (ctaCopy) {
      return (
        <div
          classList={{
            "dark:opacity-30": !isPaymentCtaEnabled(),
          }}
          class="w-full text-center text-mediumBold text-baseTertiaryLight"
        >
          {ctaCopy}
        </div>
      );
    }

    return (
      <div
        class={`flex w-full text-baseTertiaryLight  ${isRewardTypePostPaid ? "justify-between" : "justify-center"}`}
        classList={{
          "dark:opacity-30": !isPaymentCtaEnabled(),
        }}
      >
        <Show when={isRewardTypePostPaid}>
          <div class="flex ">
            <div class="text-mediumBold ">Pay ₹{dueAmount()}</div>
            <Show when={dueAmount() != selectedAmount()}>
              <div class="pl-1 text-medium text-textNormal line-through">
                ₹{selectedAmount()}
              </div>
            </Show>
          </div>
        </Show>
        <div class="flex items-center justify-center">
          <div class="text-mediumBold ">
            {offerConstruct?.type === "SUBSCRIPTION_REWARD"
              ? "Start your monthly savings"
              : "Get this card"}
          </div>
          <Show when={isRewardTypePostPaid}>
            <PhosphorIcon
              name="caret-right"
              size="bold"
              fontSize={13}
              class="px-2 "
            />
          </Show>
        </div>
      </div>
    );
  }

  function isPaymentCtaEnabled() {
    return (
      ctaState() != "DISABLED" &&
      selectedAmount() != null &&
      errorMessage() == null &&
      enoughCoinsAvailable()
    );
  }

  function onClickGetThisCard() {
    analyticsService.logEvent(HubbleEvent.CLICK_GET_THIS_CARD, {
      amount: selectedAmount(),
      brandName: brand.name,
    });

    const _initiateOrder = () =>
      initiateOrder({
        selectedAmount: selectedAmount,
        selectedVariantId: internalState.selectedVariantId,
        rewardDiscountAmount: rewardDiscountAmount,
        dueAmount: dueAmount,
        redemptionAmount: internalState.redemptionAmount,
        discountDetails: internalState.discountDetails,
        dueAmount2: internalState.dueAmount2,
        discountDetails2: internalState.discountDetails2,
        redemptionAmount2: internalState.redemptionAmount2,
        offerConstructTenure: internalState.offerConstructTenure,
        ctaState: ctaState,
        isSimulating: internalState.isSimulating,
        useRewardCoins: internalState.useRewardCoins,
        hubbleCoinRewards: internalState.hubbleCoinRewards,
        initialErrorOnSimulate: internalState.initialErrorOnSimulate,
        dryRunErrorMessage: errorMessage,
        voucherCount: internalState.voucherCount,
      });

    if (dueAmount() === 0 && !offerConstruct) {
      showConfirmationDialog(_initiateOrder);
    } else {
      _initiateOrder();
    }
  }

  function showConfirmationDialog(onConfirm: () => void) {
    updateModalContentGetter(() => {
      return (
        <ConfirmationDialog
          title={`${toRupees(selectedAmount() ?? 0)} ${brand.voucherProductMetadata.title} card`}
          message={
            <div class="flex flex-row items-center text-normal text-textDark ">
              {`You will be using`}
              <img src={coinIcon} class="ml-[6px] mr-[4px] h-5 w-5" />
              <span class="font-semibold text-[#E89901]">
                {rewardDiscountAmount()}
              </span>
            </div>
          }
          logoUrl={brand.voucherProductMetadata.logoUrl}
          onConfirm={() => {
            setIsModalOn(false);
            setOuterFrameClass("");
            onConfirm();
          }}
          onCancel={() => {
            setIsModalOn(false);
            setOuterFrameClass("");
          }}
        />
      );
    });

    setOuterFrameClass("lg:w-[416px]");
    setIsModalOn(true);
  }
}
